import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, CalendarCheck, Heart, Sparkles, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { getClient, postCheckin, scegliProposta, AREA_LABEL, OBIETTIVO_LABEL, CLUB_BASE } from "./clubApi";
import { Btn, Card, ScoreRing, Bar, Pill, Footer, Logo, inputCls } from "./ui";
import { setMyToken, clearMyToken } from "./device";
import InstallGuide from "./InstallGuide";
import PushCard from "./PushCard";
import Routine from "./Routine";
import Badges from "./Badges";
import ProgressChart from "./ProgressChart";

const fmt = (iso) => new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" });

const Checkin = ({ token, onDone }) => {
  const [v, setV] = useState({ energia: 3, sonno: 3, costanza: 3, nota: "" });
  const [busy, setBusy] = useState(false);
  const Row = ({ k, label }) => (
    <div>
      <p className="text-xs uppercase tracking-[0.18em] text-[#9A948A] mb-2">{label}</p>
      <div className="grid grid-cols-5 gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" data-testid={`checkin-${k}-${n}`} onClick={() => setV({ ...v, [k]: n })}
            className={`h-11 rounded-xl border font-club-display text-lg ${v[k] === n ? "border-[#E5B13A] bg-[#E5B13A] text-[#15120A]" : "border-[#2E2E34] bg-[#0E0E10]"}`}>{n}</button>
        ))}
      </div>
    </div>
  );
  const send = async () => {
    setBusy(true);
    try { const ck = await postCheckin(token, v); toast.success("Check salvato. Grazie per esserci!"); onDone(ck); } catch { toast.error("Non riesco a salvare, riprova"); }
    setBusy(false);
  };
  return (
    <Card className="space-y-5" data-testid="checkin-form">
      <div className="flex items-center gap-2"><CalendarCheck size={18} className="text-[#E5B13A]" /><p className="font-semibold">Com'è andata questa settimana?</p></div>
      <p className="text-xs text-[#9A948A] -mt-3">Rispondi d'istinto, non c'è un voto giusto. Serve solo a capire come stai.</p>
      <Row k="energia" label="Energia" /><Row k="sonno" label="Sonno" /><Row k="costanza" label="Quanto sei riuscita a seguire piano e abitudini" />
      <textarea className={`${inputCls} min-h-[72px]`} placeholder="Vuoi raccontare qualcosa alla tua incaricata? (facoltativo)" value={v.nota} onChange={(e) => setV({ ...v, nota: e.target.value })} data-testid="checkin-nota" />
      <Btn className="w-full" disabled={busy} onClick={send} data-testid="checkin-submit">Salva il check</Btn>
    </Card>
  );
};

export default function ClientPage() {
  const { token } = useParams();
  const [sp, setSp] = useSearchParams();
  const nuovo = sp.get("benvenuto") === "1";
  const gia = sp.get("gia") === "1";
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [showCk, setShowCk] = useState(false);
  const [choosing, setChoosing] = useState("");

  const scegli = async (p) => {
    if (!window.confirm(`Scegli «${p.titolo}»? Da questo momento il tuo percorso parte.`)) return;
    setChoosing(p.id);
    try { const out = await scegliProposta(token, p.id); setD(out); toast.success("Ottima scelta! Il tuo percorso è attivo."); } catch { toast.error("Non riesco a salvare, riprova"); }
    setChoosing("");
  };

  useEffect(() => {
    getClient(token).then((x) => { setD(x); setMyToken(token); }).catch((e) => {
      if (e?.response?.status === 404) clearMyToken();
      setErr("Pagina non trovata. Controlla il link che ti ha mandato la tua incaricata.");
    });
    const l = document.createElement("link");
    l.rel = "manifest"; l.href = `${process.env.REACT_APP_BACKEND_URL}/api/club/manifest/${token}`; l.id = "club-manifest";
    document.getElementById("club-manifest")?.remove();
    document.head.appendChild(l);
    return () => l.remove();
  }, [token]);

  if (err) return (
    <div className="mx-auto max-w-md px-6 pt-24 text-center text-[#9A948A]" data-testid="client-error">
      <p>{err}</p>
      {gia && <Link to={`${CLUB_BASE}/i/${sp.get("da") || ""}?rifai=1`} className="mt-4 inline-block text-sm text-[#F5D27A]" data-testid="redo-test-link">Rifai il test</Link>}
    </div>
  );
  if (!d) return <div className="min-h-screen flex items-center justify-center"><div className="w-6 h-6 border-2 border-[#E5B13A] border-t-transparent rounded-full animate-spin" /></div>;

  const { profilo, piano, checkins, incaricata, nome } = d;
  const first = nome.split(" ")[0];
  const incFirst = incaricata?.name?.split(" ")[0] || "la tua incaricata";
  const last = checkins[checkins.length - 1];
  const current = last ? last.punteggio : profilo.indice;
  const daysSince = last ? Math.floor((Date.now() - new Date(last.data)) / 86400000) : 99;
  const hour = new Date().getHours();
  const saluto = hour < 13 ? "Buongiorno" : hour < 18 ? "Buon pomeriggio" : "Buonasera";

  return (
    <div className="mx-auto max-w-md px-6 pt-8 pb-10" data-testid="client-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3"><Logo size={32} /><span className="font-club-display text-lg">Diamond Club</span></div>
        {incaricata?.picture && <img src={incaricata.picture} alt="" className="h-8 w-8 rounded-full object-cover" referrerPolicy="no-referrer" />}
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-8">
        <p className="text-xs uppercase tracking-[0.2em] text-[#9A948A]">{nuovo ? `Grazie ${first}` : saluto}</p>
        <h1 className="font-club-display text-4xl leading-[1.05] mt-1">{nuovo ? <>Ecco <span className="text-[#E5B13A]">da dove parti.</span></> : <>{first}, <span className="text-[#E5B13A]">siamo con te.</span></>}</h1>
      </motion.div>

      {gia && (
        <Card className="mt-6 border-[#E5B13A]/30 flex gap-3 items-start" data-testid="already-done-card">
          <Sparkles size={18} className="text-[#E5B13A] mt-0.5 shrink-0" />
          <div className="text-sm flex-1">
            <p className="font-semibold">Il test l'hai già fatto: questa è la tua pagina</p>
            <p className="text-[#9A948A] mt-1">Qui trovi punteggio, routine e le proposte di {incFirst}. Salvala sul telefono (vedi in fondo) così non la perdi più.</p>
            <div className="mt-3 flex flex-wrap gap-3 items-center">
              <button onClick={() => setSp({}, { replace: true })} className="text-xs text-[#F5D27A]" data-testid="already-done-ok">Ok, ho capito</button>
              {d.incaricata_slug && <Link to={`${CLUB_BASE}/i/${d.incaricata_slug}?rifai=1`} className="text-xs text-[#6F6A62] inline-flex items-center gap-1" data-testid="redo-test-link"><RotateCcw size={11} /> Voglio rifare il test da zero</Link>}
            </div>
          </div>
        </Card>
      )}

      {nuovo && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <Card className="mt-6 border-[#E5B13A]/30" data-testid="new-result-card">
            <div className="flex flex-col items-center text-center">
              <ScoreRing value={profilo.indice} />
              <div className="mt-4 flex flex-wrap justify-center gap-2">{(d.risposte?.obiettivi || []).map((o) => <Pill key={o}>{OBIETTIVO_LABEL[o] || o}</Pill>)}</div>
              <p className="mt-4 text-sm text-[#9A948A]">Questa è la <b className="text-[#F4EFE6]">tua pagina personale</b>: resta qui, con il tuo percorso. <b className="text-[#F4EFE6]">Salvala sul telefono adesso</b> (ci vogliono 20 secondi, vedi in fondo): se chiudi e riapri il link del test, torni comunque qui.</p>
            </div>
          </Card>
        </motion.div>
      )}

      {incaricata && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="mt-6 border-[#E5B13A]/25 bg-gradient-to-br from-[#1A1710] to-[#141417]" data-testid="welcome-card">
            <div className="flex items-center gap-3">
              {incaricata.picture ? <img src={incaricata.picture} alt="" className="h-12 w-12 rounded-full object-cover ring-2 ring-[#E5B13A]/40" referrerPolicy="no-referrer" /> : <div className="h-12 w-12 rounded-full bg-[#E5B13A]/20 flex items-center justify-center text-[#E5B13A]"><Heart size={18} /></div>}
              <div><p className="text-[0.65rem] uppercase tracking-[0.2em] text-[#9A948A]">Un messaggio da chi ti segue</p><p className="font-semibold" data-testid="welcome-inc-name">{incaricata.name}</p></div>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-[#F4EFE6]/90 whitespace-pre-line" data-testid="welcome-text">{incaricata.benvenuto}</p>
            {incaricata.whatsapp && (
              <a href={`https://wa.me/${incaricata.whatsapp}?text=${encodeURIComponent(`Ciao ${incFirst}, ti scrivo dalla mia pagina Diamond Club.`)}`} target="_blank" rel="noreferrer" data-testid="client-wa-link">
                <Btn variant="wa" className="mt-4 w-full"><MessageCircle size={16} /> Scrivi a {incFirst} su WhatsApp</Btn>
              </a>
            )}
          </Card>
        </motion.div>
      )}

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mt-8 flex items-center gap-5">
        <ScoreRing value={current} size={132} label="oggi" />
        <div>
          <p className="font-club-display text-2xl leading-tight">{piano ? "Il tuo percorso è attivo." : d.proposte?.length ? "Le proposte sono pronte." : "Il tuo percorso sta arrivando."}</p>
          <p className="text-sm text-[#9A948A] mt-2">{last ? "Questo numero racconta come stai andando. Guardalo con gentilezza: conta la direzione, non il singolo giorno." : "Questo è il tuo punto di partenza. Da qui si può solo crescere."}</p>
          {last && <p className="text-xs text-[#9A948A] mt-2" data-testid="score-delta">Partenza {profilo.indice} → oggi {current} ({current - profilo.indice >= 0 ? "+" : ""}{current - profilo.indice})</p>}
        </div>
      </motion.div>

      <Routine d={d} onUpdate={setD} />

      {piano ? (
        <Card className="mt-8" data-testid="client-piano">
          <div className="flex items-center justify-between"><p className="font-semibold">Il tuo percorso di {piano.durata_giorni || 30} giorni</p><Pill tone="green">attivo</Pill></div>
          <p className="text-xs text-[#9A948A] mt-1">Preparato apposta per te da {incFirst}, sulle tue risposte.</p>
          <div className="mt-4 space-y-3">
            {piano.prodotti.map((p) => (
              <div key={p.codice || p.nome} className="rounded-2xl bg-[#0E0E10] border border-[#232328] p-3.5" data-testid="piano-prodotto">
                <p className="font-medium text-sm">{p.nome}</p>
                {p.dose && <p className="text-xs text-[#9A948A] mt-1">{p.dose}</p>}
                {p.fine_stimata && <p className="text-[0.65rem] uppercase tracking-[0.15em] text-[#6F6A62] mt-2">finisce intorno al {fmt(p.fine_stimata)}</p>}
              </div>
            ))}
          </div>
          {piano.note && <p className="mt-4 text-sm text-[#F4EFE6]/90 italic border-l-2 border-[#E5B13A] pl-3">"{piano.note}" — {incFirst}</p>}
          <p className="mt-4 text-xs text-[#9A948A]">Per i prodotti e gli ordini ci pensa {incFirst}: scrivile quando vuoi, anche solo per una domanda.</p>
        </Card>
      ) : d.proposte?.length > 0 ? (
        <div className="mt-8" data-testid="client-proposte">
          <p className="font-club-display text-2xl">{incFirst} ti propone {d.proposte.length === 1 ? "un percorso" : `${d.proposte.length} percorsi`}</p>
          <p className="text-sm text-[#9A948A] mt-1">{d.proposte.length === 1 ? "Guardalo con calma: se ti senti pronta, confermalo e si parte." : "Guardali con calma e scegli quello che senti più tuo. Se hai dubbi, scrivile: è lì per questo."}</p>
          <div className="mt-4 space-y-3">
            {d.proposte.map((p) => (
              <Card key={p.id} className="border-[#E5B13A]/25" data-testid={`proposta-card-${p.id}`}>
                <div className="flex items-center justify-between"><p className="font-semibold">{p.titolo}</p><Pill tone="muted">{p.durata_giorni} giorni</Pill></div>
                <div className="mt-3 space-y-2">
                  {p.prodotti.map((x) => (
                    <div key={x.codice || x.nome} className="rounded-2xl bg-[#0E0E10] border border-[#232328] p-3">
                      <p className="font-medium text-sm">{x.nome}</p>
                      {x.dose && <p className="text-xs text-[#9A948A] mt-0.5">{x.dose}</p>}
                    </div>
                  ))}
                </div>
                {p.note && <p className="mt-3 text-sm text-[#F4EFE6]/90 italic border-l-2 border-[#E5B13A] pl-3">"{p.note}" — {incFirst}</p>}
                <Btn className="mt-4 w-full" onClick={() => scegli(p)} disabled={!!choosing} data-testid={`proposta-scegli-${p.id}`}>{choosing === p.id ? "Un attimo…" : d.proposte.length === 1 ? "Confermo, si parte" : "Scelgo questo"}</Btn>
              </Card>
            ))}
          </div>
        </div>
      ) : (
        <Card className="mt-8 border-dashed" data-testid="client-piano-pending">
          <p className="font-semibold">{incFirst} ti scrive a breve su WhatsApp</p>
          <p className="text-sm text-[#9A948A] mt-1">Con le informazioni che hai lasciato capirete insieme qual è il percorso più adatto a te. Le sue proposte compariranno qui. Intanto, le tre abitudini qui sopra sono già tue: contano più di qualsiasi prodotto.</p>
        </Card>
      )}

      <div className="mt-8">
        <div className="flex items-center justify-between">
          <p className="font-club-display text-2xl">Come va</p>
          {piano && !showCk && <Btn variant={daysSince >= 7 ? "gold" : "ghost"} className="px-4 py-2 text-xs" onClick={() => setShowCk(true)} data-testid="open-checkin-btn">Fai il check</Btn>}
        </div>
        {!piano && <Card className="mt-4 text-sm text-[#9A948A]" data-testid="check-not-yet">Il primo check arriva dopo la prima settimana di percorso. Nessuna fretta: per ora bastano le abitudini.</Card>}
        {showCk && <div className="mt-4"><Checkin token={token} onDone={(ck) => { setD({ ...d, checkins: [...checkins, ck] }); setShowCk(false); }} /></div>}
        {checkins.length > 0 && <ProgressChart start={profilo.indice} startDate={d.created_at} checkins={checkins} />}
        {checkins.length > 0 && (
          <Card className="mt-4 space-y-2" data-testid="checkin-history">
            {[...checkins].reverse().slice(0, 6).map((c) => (
              <div key={c.data} className="flex items-center justify-between text-sm border-b border-[#232328] last:border-0 pb-2 last:pb-0">
                <span className="text-[#9A948A]">{fmt(c.data)}</span>
                <span className="text-xs text-[#9A948A]">E{c.energia} · S{c.sonno} · C{c.costanza}</span>
                <span className="font-club-display text-lg text-[#E5B13A]">{c.punteggio}</span>
              </div>
            ))}
          </Card>
        )}
      </div>

      <Badges badges={d.badges} />

      <Card className="mt-8 space-y-3">
        <p className="text-xs uppercase tracking-[0.2em] text-[#9A948A] mb-1">Le tue aree alla partenza</p>
        {Object.entries(profilo.aree).map(([k, v]) => <Bar key={k} label={AREA_LABEL[k] || k} value={v} />)}
      </Card>

      <div className="mt-8 space-y-3">
        <PushCard token={token} incFirst={incFirst} />
        <InstallGuide compact={!nuovo} />
      </div>
      <Footer />
    </div>
  );
}
